import { scrollToNavigationBar } from 'src/domains/casino/utils/utils';
import { BreakpointsState } from 'src/domains/layouts/state/breakpointsState/BreakpointsState';
import { assertNever } from 'src_common/common/assertNever';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { GamePlayTagType } from 'src/domains/casino/utils/callbackTypes';
import { CasinoGameModelExt } from './store/game-store/CasinoGameModelExt';
import { Common } from 'src/domains/common/Common';
import { LocalStorageState } from 'src/domains/layouts/state/localStorage/LocalStorageState';
import { CollectionType } from 'src/domains/casino/types/types';
import { CasinoGameModeParamType } from 'src/domains/layouts/state/router/newRouter/mainRouteTypes';
import { GoogleTagManagerState } from 'src/domains/layouts/state/googleState/GoogleTagManagerState';

export class RedirectState {
    private starRouter: StarRouter;
    private breakpointsState: BreakpointsState;

    public constructor(private readonly common: Common) {
        this.starRouter = StarRouter.get(this.common);
        this.breakpointsState = BreakpointsState.get(this.common);
    }

    public getIsUserAutorized = (): boolean => {
        return this.common.session.isAuthorized;
    };

    public get currentView(): string | undefined {
        return this.starRouter.currentView?.name;
    }

    public goToLoginForm = (): boolean => {
        if (this.getIsUserAutorized() === false) {
            // TODO: setTimeout to remove after starRouter inner state change to mobx
            setTimeout(() => {
                this.starRouter.redirectToLogin();
            }, 0);
            return true;
        }

        return false;
    };

    public redirectToLogin = (): void => {
        this.starRouter.redirectToLogin();
    };

    public onClickLogin = (): void => {
        this.starRouter.redirectToLogin();
    };

    public redirectToCasinoPage = (): void => {
        this.starRouter.redirectToCasinoPage();
        window.scrollTo(0, 0);
    };

    public redirectToLiveCasinoPage = (): void => {
        this.starRouter.redirectToLiveCasinoPage();
        window.scrollTo(0, 0);
    };

    public redirectToVirtualsPage = (): void => {
        this.starRouter.redirectToVirtualsPage();
        window.scrollTo(0, 0);
    };

    /**
     * @deprecated - Please use RedirectState.redirectToCollectionTab
     */
    public redirectToCasinoTab = (id: string | number, navigationOffset?: number): void => {
        this.starRouter.redirectToCasinoTabPage(id.toString());
        scrollToNavigationBar(this.breakpointsState, navigationOffset);
    };

    /**
     * @deprecated - Please use RedirectState.redirectToCollectionTab
     */
    public redirectToLiveCasinoTab = (id: string | number, navigationOffset?: number): void => {
        this.starRouter.redirectToLiveCasinoTabPage(id.toString());
        scrollToNavigationBar(this.breakpointsState, navigationOffset);
    };

    public redirectToCollectionTab = (
        collectionType: CollectionType,
        tabId: string,
        navigationOffset?: number
    ): void => {
        if (collectionType === 'casino') {
            this.starRouter.redirectToCasinoTabPage(tabId);
            scrollToNavigationBar(this.breakpointsState, navigationOffset);
            return;
        }

        if (collectionType === 'live-casino') {
            this.starRouter.redirectToLiveCasinoTabPage(tabId);
            scrollToNavigationBar(this.breakpointsState, navigationOffset);
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (collectionType === 'virtuals') {
            return; // No tabs in Virtuals
        }

        return assertNever('redirectToCollectionTab', collectionType);
    };

    public redirectToGameCollection = (gameModel: CasinoGameModelExt): void => {
        if (gameModel.collection === 'live-casino') {
            this.redirectToLiveCasinoPage();
        } else if (gameModel.collection === 'virtuals') {
            this.redirectToVirtualsPage();
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        } else if (gameModel.collection === 'casino') {
            this.redirectToCasinoPage();
        } else {
            assertNever('Redirect to collection', gameModel.collection);
        }
    };

    public onHandleUrlRedirection = (redirectUrl: string): void => {
        this.starRouter.handleUrlRedirection(redirectUrl);
        window.scrollTo(0, 0);
    };

    public redirectToHomepage = (): void => {
        this.starRouter.redirectToHomepage();
    };

    public redirectToTransactionHistory = (): void => {
        this.starRouter.redirectToTransactionHistory();
    };

    public redirectToCasinoGameInfo = (): void => {
        this.starRouter.redirectToCasinoGameInfo();
    };

    public redirectWithoutAccountParam = (): void => {
        return this.starRouter.closeAccount();
    };

    public redirectToCasinoBannerTerms = (): void => {
        return this.starRouter.redirectToCasinoBannerTerms();
    };

    public redirectToCasinoGame = (
        gameId: number,
        collection: CollectionType,
        mode?: CasinoGameModeParamType
    ): void => {
        this.starRouter.redirectToCasinoGame(gameId, collection, mode);
    };

    public getHomePage = (collection: CollectionType): string => {
        const homeAddress = this.starRouter.buildUrlTo({ name: collection }).substr(1);
        return homeAddress;
    };

    // ------------- Callbacks Gateway -------------

    public redirectToDepositPage = (): void => {
        const googleTagManager = GoogleTagManagerState.get(this.common);
        googleTagManager.routingAccountChange('top-up');
        this.starRouter.redirectToTopUp(null);
    };

    public onGoogleTagManagerGamePlayTag = (data: GamePlayTagType): void => {
        const googleTagManager = GoogleTagManagerState.get(this.common);
        googleTagManager.gamePlayTag(data.gameId, data.gameName, data.gameType);
    };

    public resetRealityCheckTime = (): void => {
        LocalStorageState.get(this.common).realityCheckTime.setValue(new Date().getTime());
    };
}
